import init, {
  WasmHandshake,
  WasmReconnect,
  fingerprint_of_pubkey,
  pub_from_priv,
  rendezvous_id_from_root,
  verify_deny_mac,
} from 'heligraph-crypto-wasm';

let inited: Promise<void> | null = null;

/** Initialize the WASM module exactly once. Subsequent calls await the same promise. */
export function ensureWasm(): Promise<void> {
  if (!inited) {
    inited = init().then(() => {
      // Discard the returned wasm export; we use named imports above.
    });
  }
  return inited;
}

export interface HandshakeBundle {
  hs: WasmHandshake;
  /** Lowercase-hex SHA-256 of agent's 32-byte static pub (informational). */
  agentFingerprint: string;
}

/** Construct a fresh WasmHandshake. Throws on malformed PIN or wrong-length keys. */
export function newHandshake(
  pin: string,
  browserStaticPriv: Uint8Array,
  agentStaticPub: Uint8Array,
): HandshakeBundle {
  const hs = new WasmHandshake(pin, browserStaticPriv, agentStaticPub);
  const agentFingerprint = fingerprint_of_pubkey(agentStaticPub);
  return { hs, agentFingerprint };
}

/** Derive the browser's fingerprint from its persistent static_priv. */
export function browserFingerprintFromPriv(priv: Uint8Array): string {
  const pub = pub_from_priv(priv);
  return fingerprint_of_pubkey(pub);
}

/** Compute an agent's display fingerprint from its 32-byte static pub. */
export function fingerprintOfAgentPub(pub: Uint8Array): string {
  return fingerprint_of_pubkey(pub);
}

/** Format a 64-char hex fingerprint as `ABCD-EF12-…` groups of 4 for UI display. */
export function formatFingerprint(hex: string): string {
  return hex.match(/.{1,4}/g)?.join('-') ?? hex;
}

export interface ReconnectBundle {
  rc: WasmReconnect;
  rendezvousId: string;
}

/**
 * Construct a WasmReconnect from the persisted reconnect root and both static keys.
 * Caller must have awaited `ensureWasm()` before calling this.
 */
export function newReconnect(
  reconnectRoot: Uint8Array,
  browserStaticPriv: Uint8Array,
  agentStaticPub: Uint8Array,
): ReconnectBundle {
  const rc = new WasmReconnect(reconnectRoot, browserStaticPriv, agentStaticPub);
  return { rc, rendezvousId: rc.rendezvous_id() };
}

/**
 * Derive the rendezvous ID (16 hex chars) from a persisted reconnect root without
 * constructing a full WasmReconnect. Caller must have awaited `ensureWasm()` first.
 */
export function rendezvousId(reconnectRoot: Uint8Array): string {
  return rendezvous_id_from_root(reconnectRoot);
}

/** Verify a deny-notice MAC. Caller must have awaited ensureWasm(). */
export function verifyDenyMac(
  reconnectRoot: Uint8Array, reason: string, rendezvousId: string, mac: Uint8Array,
): boolean {
  return verify_deny_mac(reconnectRoot, reason, rendezvousId, mac);
}
